import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Star } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReviewForm({ eventId = null, onSubmitSuccess }) {
  const [formData, setFormData] = useState({
    reviewer_name: '',
    reviewer_email: '',
    rating: 0,
    comment: ''
  });
  const [hoverRating, setHoverRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.rating === 0) {
      toast.error('Per favore, seleziona una valutazione');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/reviews`, {
        event_id: eventId,
        reviewer_name: formData.reviewer_name,
        reviewer_email: formData.reviewer_email,
        rating: formData.rating,
        comment: formData.comment
      });

      toast.success('Grazie! La tua recensione è stata inviata e sarà pubblicata dopo moderazione.');
      
      // Reset form
      setFormData({
        reviewer_name: '',
        reviewer_email: '',
        rating: 0,
        comment: ''
      });
      
      if (onSubmitSuccess) {
        onSubmitSuccess();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'invio della recensione');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Lascia una Recensione</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Rating Stars */}
          <div>
            <Label>Valutazione *</Label>
            <div className="flex gap-2 mt-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setFormData({ ...formData, rating: star })}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="focus:outline-none transition-transform hover:scale-110"
                >
                  <Star
                    className={`w-8 h-8 ${
                      star <= (hoverRating || formData.rating)
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-gray-300'
                    }`}
                  />
                </button>
              ))}
              {formData.rating > 0 && (
                <span className="ml-2 text-gray-600 self-center">
                  {formData.rating} {formData.rating === 1 ? 'stella' : 'stelle'}
                </span>
              )}
            </div>
          </div>

          <div>
            <Label htmlFor="reviewer_name">Nome *</Label>
            <Input
              id="reviewer_name"
              value={formData.reviewer_name}
              onChange={(e) => setFormData({ ...formData, reviewer_name: e.target.value })}
              required
              placeholder="Il tuo nome"
            />
          </div>

          <div>
            <Label htmlFor="reviewer_email">Email *</Label>
            <Input
              id="reviewer_email"
              type="email"
              value={formData.reviewer_email}
              onChange={(e) => setFormData({ ...formData, reviewer_email: e.target.value })}
              required
              placeholder="tua@email.com"
            />
          </div>

          <div>
            <Label htmlFor="comment">Commento *</Label>
            <Textarea
              id="comment"
              value={formData.comment}
              onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
              required
              placeholder="Racconta la tua esperienza..."
              rows={4}
            />
          </div>

          <Button 
            type="submit" 
            className="w-full bg-purple-600 hover:bg-purple-700"
            disabled={submitting}
          >
            {submitting ? 'Invio...' : 'Invia Recensione'}
          </Button>

          <p className="text-sm text-gray-500 text-center">
            La tua recensione sarà visibile dopo l'approvazione del team
          </p>
        </form>
      </CardContent>
    </Card>
  );
}
